import React, { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

const MOOD_OPTIONS = [
  { label: "Much worse", emoji: "😔", value: "much_worse" },
  { label: "A little worse", emoji: "😕", value: "a_little_worse" },
  { label: "About the same", emoji: "😐", value: "same" },
  { label: "A little better", emoji: "🙂", value: "a_little_better" },
  { label: "Much better", emoji: "😊", value: "much_better" },
];

interface MoodCheckInProps {
  conversationLength: number;
  onSelect: (mood: string) => void;
}

const MoodCheckIn: React.FC<MoodCheckInProps> = ({ conversationLength, onSelect }) => {
  const [selected, setSelected] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const handleSelect = async (value: string) => {
    if (selected) return;
    setSelected(value);
    setSaving(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from("mood_checkins" as any).insert({
          user_id: user.id,
          mood: value,
          conversation_length: conversationLength,
        } as any);
      }
    } catch (e) {
      console.error("Failed to save mood check-in:", e);
      toast({ title: "Couldn't save", description: "Your response wasn't saved, but that's ok.", variant: "destructive" });
    } finally {
      setSaving(false);
      onSelect(value);
    }
  };

  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {MOOD_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          onClick={() => handleSelect(opt.value)}
          disabled={!!selected || saving}
          className={cn(
            "px-3 py-2 rounded-xl text-sm font-medium transition-all",
            "border border-border/50",
            selected === opt.value
              ? "bg-primary/30 border-primary text-foreground scale-105"
              : selected
                ? "opacity-40 cursor-default"
                : "hover:bg-primary/20 hover:border-primary/50 cursor-pointer",
          )}
          style={{
            WebkitBackdropFilter: "blur(24px)",
            backdropFilter: "blur(24px)",
            background: selected === opt.value
              ? "rgba(139, 92, 246, 0.35)"
              : "rgba(0, 0, 0, 0.3)",
          }}
        >
          <span className="mr-1">{opt.emoji}</span>
          {opt.label}
        </button>
      ))}
    </div>
  );
};

export default MoodCheckIn;
